import React from 'react';
import ToggleSwitch from '../../../../components/common/ToggleSwitch';
import CustomButton from '../../../../components/common/CustomButton';
import { HiClock, HiOutlineBell, HiPlusCircle, HiHashtag, HiUser, } from 'react-icons/hi';

type FrequencyColors = { bg: string; text: string };

type Trigger = {
  id: string;
  entityTypeLabel: string;
  frequency: string;
  details: string;
  send_to: string[];
  enabled: boolean;
};

type Props = {
  triggers: Trigger[];
  onEdit: (trigger: any) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  getFrequencyBadgeColor: (frequency: any) => FrequencyColors;
  canAdd: boolean;
  onAddClick: () => void;
};

const TriggersList: React.FC<Props> = ({
  triggers,
  onEdit,
  onToggle,
  onDelete,
  getFrequencyBadgeColor,
  canAdd,
  onAddClick
}) => {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white font-['Inter']">
          {triggers.length} {Number(triggers.length) === 1 ? 'trigger' : 'triggers'} configured
        </h4>
        <CustomButton
          onClick={onAddClick}
          size="sm"
          leftIcon={<HiPlusCircle className="text-lg" />}
          disabled={!canAdd}
        >
          Add Trigger
        </CustomButton>
      </div>
      <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
        {triggers.map((trigger) => {
          const frequencyColors = getFrequencyBadgeColor(trigger.frequency as any);
          return (
            <div
              key={trigger.id}
              onClick={() => onEdit(trigger)}
              className="bg-white dark:bg-secondary-dark rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-4 w-full">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex-shrink-0">
                  <HiOutlineBell className="text-lg" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
                  <div className="flex items-center gap-3">
                    <h5 className="text-sm font-semibold text-gray-900 dark:text-white font-['Inter']">
                      {trigger.entityTypeLabel}
                    </h5>
                    <div className={`${frequencyColors.bg} ${frequencyColors.text} px-2.5 py-1 rounded-full text-xs font-medium capitalize flex-shrink-0 inline-flex items-center gap-1.5`}>
                      <HiClock className="text-xs" />
                      <span>{trigger.frequency}</span>
                    </div>
                    <div className="flex-1"></div>
                  </div>
                  <div className="flex items-center gap-4 w-full">
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-['Inter']">
                      {trigger.details}
                    </p>
                    {trigger.send_to.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 items-center flex-1">
                        {trigger.send_to.slice(0, 6).map((label: string, idx: number) => {
                          const isChannel = label.startsWith('#');
                          const isUser = label.startsWith('@');
                          return (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300"
                            >
                              {isChannel ? (
                                <>
                                  <HiHashtag className="text-xs" />
                                  <span>{label.substring(1)}</span>
                                </>
                              ) : isUser ? (
                                <>
                                  <HiUser className="text-xs" />
                                  <span>{label.substring(1)}</span>
                                </>
                              ) : (
                                <span>{label}</span>
                              )}
                            </span>
                          );
                        })}
                        {trigger.send_to.length > 6 && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                            +{trigger.send_to.length - 6}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <ToggleSwitch
                    id={`trigger-${trigger.id}`}
                    name={`trigger-${trigger.id}`}
                    label=""
                    checked={trigger.enabled}
                    onChange={() => onToggle(trigger.id)}
                    className="flex items-center"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(trigger.id);
                    }}
                    className="p-2 mb-0.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-300 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center"
                    title="Delete trigger"
                  >
                    {/* icon removed to avoid extra imports; unicode cross */}
                    <span className="text-lg">✕</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TriggersList;